import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../api";

const getStatus = (ev) => {
  const now   = new Date();
  const end   = ev.endDate   ? new Date(ev.endDate)   : null;
  const start = ev.startDate ? new Date(ev.startDate) : null;
  if (end && now > end)     return "ended";
  if (start && now < start) return "upcoming";
  return "ongoing";
};

const STATUS_META = {
  ongoing:  { label: "Ongoing",  color: "#059669", bg: "#ECFDF5", border: "#A7F3D0", accent: "#10B981" },
  upcoming: { label: "Upcoming", color: "#1D9AEC", bg: "#EFF8FF", border: "#BFDBFE", accent: "#1D9AEC" },
  ended:    { label: "Ended",    color: "#94A3B8", bg: "#F1F5F9", border: "#E2E8F0", accent: "#CBD5E1" },
};

const formatDate = (d) => d
  ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
  : "Date TBA";

function EventCard({ ev, idx, navigate }) {
  const status = getStatus(ev);
  const meta   = STATUS_META[status];
  const isEnded = status === "ended";

  return (
    <div
      className="card-hover"
      onClick={() => navigate(`/admin/events/${ev._id}/nominations`)}
      style={{
        ...cardBase,
        opacity: isEnded ? 0.72 : 1,
        animationDelay: `${idx * 30}ms`,
        animation: "fadeIn 0.25s ease forwards",
        borderTop: `3px solid ${meta.accent}`,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 9,
          background: `${meta.accent}15`, border: `1px solid ${meta.accent}30`,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17,
        }}>📅</div>
        <span style={{
          fontSize: 10, fontWeight: 700, color: meta.color,
          background: meta.bg, border: `1px solid ${meta.border}`,
          padding: "3px 9px", borderRadius: 20,
          letterSpacing: "0.05em", textTransform: "uppercase",
        }}>{meta.label}</span>
      </div>

      <h3 style={{ margin: "0 0 5px", fontSize: 14, fontWeight: 600, color: isEnded ? "#64748B" : "#0F172A", lineHeight: 1.35 }}>
        {ev.eventName}
      </h3>
      <p style={{ margin: "0 0 14px", fontSize: 11.5, color: "#94A3B8" }}>
        {formatDate(ev.startDate)}
        {ev.endDate ? ` – ${formatDate(ev.endDate)}` : ""}
      </p>

      <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: isEnded ? "#94A3B8" : meta.accent }}>
        View Nominations →
      </div>
    </div>
  );
}

function SectionHeader({ title, count, icon, color, bg, border, collapsed, onToggle, collapsible }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "10px 16px", background: bg,
      border: `1px solid ${border}`, borderRadius: 10,
      marginBottom: collapsed ? 0 : 16,
    }}>
      <span style={{ fontSize: 18 }}>{icon}</span>
      <span style={{ fontSize: 14, fontWeight: 700, color }}>{title}</span>
      <span style={{
        fontSize: 11, fontWeight: 700, color,
        background: `${color}18`, border: `1px solid ${color}30`,
        padding: "2px 9px", borderRadius: 20,
      }}>{count}</span>
      <span style={{ flex: 1 }} />
      {collapsible && (
        <button
          onClick={onToggle}
          style={{
            background: "none", border: `1px solid ${border}`, borderRadius: 7,
            padding: "4px 13px", cursor: "pointer",
            fontSize: 12, fontWeight: 600, color,
          }}
        >
          {collapsed ? "Show ▾" : "Hide ▴"}
        </button>
      )}
    </div>
  );
}

export default function AdminEventsPage() {
  const navigate = useNavigate();
  const [events,      setEvents]      = useState(null);
  const [search,      setSearch]      = useState("");
  const [showEnded,   setShowEnded]   = useState(false);

  useEffect(() => {
    api.get("/jury/events")
      .then(res => setEvents(res.data.data || []))
      .catch(() => setEvents([]));
  }, []);

  const { ongoing, upcoming, ended } = useMemo(() => {
    if (!events) return { ongoing: [], upcoming: [], ended: [] };
    const q = search.trim().toLowerCase();
    const filtered = q
      ? events.filter(ev => ev.eventName.toLowerCase().includes(q))
      : events;
    return {
      ongoing:  filtered.filter(ev => getStatus(ev) === "ongoing"),
      upcoming: filtered.filter(ev => getStatus(ev) === "upcoming"),
      ended:    filtered.filter(ev => getStatus(ev) === "ended"),
    };
  }, [events, search]);

  const active = [...ongoing, ...upcoming];

  return (
    <Layout title="Events" subtitle="Select an event to manage its nominations">
      <button
        onClick={() => navigate("/admin")}
        style={{ marginBottom: 18, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#475569", display: "inline-flex", alignItems: "center", gap: 6, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
      >
        ← Back
      </button>

      {/* ── Stats + Search row ── */}
      {events && events.length > 0 && (
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap", alignItems: "center" }}>
          {/* Stat chips */}
          {[
            { label: "Ongoing",  count: events.filter(e => getStatus(e) === "ongoing").length,  color: "#059669", bg: "#ECFDF5" },
            { label: "Upcoming", count: events.filter(e => getStatus(e) === "upcoming").length, color: "#1D9AEC", bg: "#EFF8FF" },
            { label: "Ended",    count: events.filter(e => getStatus(e) === "ended").length,    color: "#94A3B8", bg: "#F1F5F9" },
          ].map(s => (
            <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 8, background: s.bg, borderRadius: 8, padding: "8px 14px" }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.count}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: s.color }}>{s.label}</span>
            </div>
          ))}

          {/* Search */}
          <div style={{ flex: 1, minWidth: 220, position: "relative", marginLeft: "auto" }}>
            <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#94A3B8" }}>🔍</span>
            <input
              type="text"
              placeholder="Search events…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: "100%", padding: "9px 14px 9px 34px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, outline: "none", background: "#fff", boxSizing: "border-box", fontFamily: "inherit", color: "#0F172A" }}
              onFocus={e => { e.target.style.borderColor = "#1D9AEC"; }}
              onBlur={e =>  { e.target.style.borderColor = "#E2E8F0"; }}
            />
            {search && (
              <button onClick={() => setSearch("")} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94A3B8", fontSize: 14 }}>✕</button>
            )}
          </div>
        </div>
      )}

      {/* Loading skeletons */}
      {!events && (
        <div style={grid}>
          {[1,2,3,4,5,6].map(i => (
            <div key={i} style={{ ...cardBase, opacity: 1 }}>
              <div className="skeleton" style={{ height: 13, width: "70%", marginBottom: 10 }} />
              <div className="skeleton" style={{ height: 10, width: "40%", marginBottom: 18 }} />
              <div className="skeleton" style={{ height: 10, width: "30%" }} />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {events && events.length === 0 && (
        <div style={{ textAlign: "center", padding: "80px 20px", background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: "#374151", margin: "0 0 6px" }}>No events found</h3>
          <p style={{ fontSize: 13, color: "#94A3B8", margin: 0 }}>Events with nomination forms will appear here.</p>
        </div>
      )}

      {/* No search results */}
      {events && events.length > 0 && active.length === 0 && ended.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 20px", background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0" }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🔍</div>
          <p style={{ fontWeight: 600, color: "#374151", margin: "0 0 4px" }}>No events match "{search}"</p>
          <button onClick={() => setSearch("")} style={{ marginTop: 10, background: "none", border: "1px solid #E2E8F0", borderRadius: 7, padding: "6px 14px", cursor: "pointer", fontSize: 13, color: "#64748B" }}>Clear search</button>
        </div>
      )}

      {events && events.length > 0 && (active.length > 0 || ended.length > 0) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>

          {/* ── Active (Ongoing + Upcoming) ── */}
          {active.length > 0 && (
            <div>
              <SectionHeader
                title="Active Events"
                count={active.length}
                icon="🟢"
                color="#059669"
                bg="#F0FDF4"
                border="#A7F3D0"
                collapsible={false}
              />
              <div style={grid}>
                {active.map((ev, idx) => (
                  <EventCard key={ev._id} ev={ev} idx={idx} navigate={navigate} />
                ))}
              </div>
            </div>
          )}

          {/* ── Ended ── */}
          {ended.length > 0 && (
            <div>
              <SectionHeader
                title="Ended Events"
                count={ended.length}
                icon="🏁"
                color="#64748B"
                bg="#F8FAFC"
                border="#E2E8F0"
                collapsible={!search}
                collapsed={!showEnded && !search}
                onToggle={() => setShowEnded(v => !v)}
              />
              {(showEnded || search) && (
                <div style={{ ...grid, marginTop: 16 }}>
                  {ended.map((ev, idx) => (
                    <EventCard key={ev._id} ev={ev} idx={idx} navigate={navigate} />
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      )}
    </Layout>
  );
}

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
  gap: 16,
};
const cardBase = {
  background: "#fff",
  border: "1px solid #E2E8F0",
  borderRadius: 10,
  padding: "18px 18px 16px",
  position: "relative",
  overflow: "hidden",
  boxShadow: "var(--shadow-sm)",
  opacity: 0,
};
